<template>
    <Modal
        v-model="commentModal"
        title="Private Anmerkung"
    >
        <ion-list>
            <ion-item lines="none">
                <ion-textarea
                    label="Anmerkung"
                    label-placement="stacked"
                    v-model="commentDraft"
                    :auto-grow="true"
                />
            </ion-item>

            <ion-item lines="none" v-if="commentLastUpdatedLabel">
                <ion-textarea
                    :model-value="'Letzte Änderung: ' + commentLastUpdatedLabel"
                    :auto-grow="false"
                    :readonly="true"
                    style="min-height: 1rem;"
                    :rows="1"
                />
            </ion-item>
        </ion-list>

        <template #actions>
            <ion-button
                color="light"
                expand="block"
                @click="onCancelComment"
                :disabled="commentSaving"
            >
                Abbrechen
            </ion-button>
            <ion-button
                color="primary"
                expand="block"
                @click="onSubmitComment"
                :disabled="commentSaving || !commentHasChanges"
            >
                Speichern
            </ion-button>
        </template>
    </Modal>
</template>


<script setup lang="ts">
import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonTextarea,
} from '@ionic/vue'
import {getAllContacts, setBookingComment} from "@/helper/booking"
import {getImageForJobsite} from '@/helper/jobsite'
import {computed, PropType, ref, Ref, watch} from "vue"
import Contact from "./Contact.vue"
import datetime from "@/helper/datetime/datetime"
import {timesheetToDoButtonLabel} from '@/helper/timesheet';
import {storeToRefs} from 'pinia';
import {useTimesheetStore} from "@/store/timesheet";
import JobsiteDetails from "@/views/JobsiteDetails.vue";
import TimesheetProcess from "@/views/Timesheet/TimesheetProcess.vue";
import {
    AbsenceType,
    Booking, BookingDetailFragmentFragment, BookingWithdrawalPotentialSuccessStats,
    BookingWithdrawalRequest, PlanningMode,
} from "@/graphql/generated/graphql";
import {getIconForAbsenceType, isShortTermSicknessDay} from "@/helper/absence";
import AutopilotWizardSickness from "@/views/components/Planning/AutopilotWizardSickness.vue";
import month from "@/helper/datetime/month";
import {pluralizeHuman, pluralizeUnitOnly} from "@/helper/amountFormatter";
import {
    fetchBookingWithdrawalPotentialSuccessStats,
    fetchWithdrawalRequests, getPredictionBasedButtonLabel,
    getPredictionStringFor, getStatusStringFor, isFinished, isPending, unclaimedKeep,
} from "@/helper/bookingWithdrawalRequest";
import Skeleton from "@/components/Skeleton.vue";
import WithdrawalModal from "@/views/components/WithdrawalModal.vue";
import {ChatStatus} from "@/helper/chat/chatStatus";
import RichResponseListItem from "@/views/Chat/RichResponses/components/RichResponseListItem.vue";
import Modal from "@/components/Modal.vue";
import {kapitelDateTimeString} from "@/graphql/kapitelTypes";
import {KapitelIcon} from "@/helper/icon";
import {useGlobalEmitter} from "@/helper/emitter";

const commentModal = defineModel({type: Boolean})

const props = defineProps({
    booking: {
        type: Object as PropType<BookingDetailFragmentFragment>,
        required: true
    }
})

const commentDraft = ref("")
const commentSaving = ref(false)
const commentText = ref<string | undefined>(undefined)
const commentLastUpdated = ref<kapitelDateTimeString | undefined>(undefined)
const commentHasChanges = computed(() => (commentDraft.value ?? "") !== (commentText.value ?? ""))
const commentLastUpdatedLabel = computed(() => {
    if (!commentLastUpdated.value) {
        return "-"
    }
    return datetime.formatKapitelDateTime(commentLastUpdated.value)
})

const syncCommentFromBooking = () => {
    commentText.value = props.booking.comment?.text ?? undefined
    commentLastUpdated.value = props.booking.comment?.lastUpdated ?? undefined
}
const syncCommentToBooking = () => {
    if (!commentLastUpdated.value) {
        return
    }
    if (!props.booking.comment) {
        props.booking.comment = {
            text: commentText.value ?? '',
            lastUpdated: commentLastUpdated.value,
        }
    } else {
        props.booking.comment.text = commentText.value ?? ''
        props.booking.comment.lastUpdated = commentLastUpdated.value
    }
}

watch(() => props.booking.comment, syncCommentFromBooking, {immediate: true})
watch(() => commentModal.value, (value) => {
    if (value) {
        commentDraft.value = commentText.value ?? ""
    }
})

const onCancelComment = () => {
    commentDraft.value = commentText.value ?? ""
    commentModal.value = false
}

const onSubmitComment = async () => {
    if (!props.booking.id || !commentHasChanges.value) {
        commentModal.value = false
        return
    }
    commentSaving.value = true
    try {
        const response = await setBookingComment(props.booking.id as number, commentDraft.value)
        if (response) {
            commentText.value = response.text ?? undefined
            commentLastUpdated.value = response.lastUpdated ?? undefined
            syncCommentToBooking()
            useGlobalEmitter().emit("day:mutated", props.booking.date)
        }
        commentModal.value = false
    } finally {
        commentSaving.value = false
    }
}
</script>

<style scoped lang="scss">

</style>
